import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { authFetch } from "../lib/auth";
import Requirements, { type ContractType } from "../components/sections/Requirements";
import type { User } from "../context/AuthProvider";
import { isRequestVerificationComplete } from "../lib/verification";
import { isRegistrationSessionKey, readRegistrationAmount } from "../lib/registrationDraft";
import { useVerificationStatus } from "../hooks/useVerificationStatus";
import VerificationStatusNotice from "../components/design/VerificationStatusNotice";
import PaymentStepFrame from "../components/design/PaymentStepFrame";

const VAT_NOTICE_VERSION = "2026-05-08.v1";
const VAT_NOTICE_TITLE = "카드결제 부가세 이중 환급 금지 안내";
const VAT_NOTICE_TEXT = [
  "본 카드결제분에 대한 부가세는 당사에서 별도 처리하지 않습니다.",
  "거래 상대방으로부터 받은 세금계산서(현금영수증) 기준으로만 환급 신청하시기 바랍니다.",
  "이중 환급 시 세무조사 대상이 될 수 있습니다.",
];
const VAT_NOTICE_TARGETS = new Set(["goods", "maintenance", "rent", "rental", "drink"]);

function vatNoticeCategoryKey(category: string | ContractType | undefined) {
  const v = String(category || "").trim().toLowerCase();
  if (v === "fee" || v === "mgmt_fee") return "maintenance";
  return v || "drink";
}

function vatNoticeStorageKey(sessionKey: string, category: string | ContractType) {
  return `julyupay:vatNotice:${sessionKey}:${vatNoticeCategoryKey(category)}:${VAT_NOTICE_VERSION}`;
}

export default function ContractStart() {
  const location = useLocation();
  const [params] = useSearchParams();
  const { account, loading, error, retry } = useVerificationStatus({ watchSearch: false });
  if (loading || error || !account) return <VerificationStatusNotice error={error} retry={retry} />;
  // Documents must never appear before the current account meets the request policy.
  if (!isRequestVerificationComplete(account)) {
    return <Navigate to={`/onboarding?next=${encodeURIComponent(location.pathname + location.search + location.hash)}`} replace />;
  }
  return <ContractStartForm key={`${account.id}:${params.get("session_key") || "new"}`} user={account} />;
}

function ContractStartForm({ user }: { user: User }) {
  const nav = useNavigate();
  const location = useLocation();
  const [sp, setSp] = useSearchParams();
  const service: ContractType = "drink";
  const [sessionKey] = useState(() => {
    const requested = sp.get("session_key");
    if (isRegistrationSessionKey(requested)) return requested;
    return typeof crypto.randomUUID === "function" ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36);
  });
  const entry = sp.get("entry");
  const entryAmount = useMemo(() => {
    if (entry !== "amount") return null;
    try { return readRegistrationAmount(window.localStorage, sessionKey); }
    catch { return null; }
  }, [sessionKey, entry]);
  const entryBackPath = `/registration?session_key=${encodeURIComponent(sessionKey)}`;
  const entryQuery = entryAmount !== null ? "&entry=amount" : "";

  // Keep existing upload sessions and verification callback URLs usable.
  useEffect(() => {
    if (sp.get("session_key") === sessionKey && !sp.has("session_ekyc")) return;
    const next = new URLSearchParams(sp);
    next.set("session_key", sessionKey);
    next.delete("session_ekyc");
    setSp(next, { replace: true });
  }, [sessionKey, sp, setSp]);

  const [extraNote, setExtraNote] = useState("");
  const [extraNoteLoaded, setExtraNoteLoaded] = useState(false);
  const [docsOk, setDocsOk] = useState(false);
  const [vatNoticeOpen, setVatNoticeOpen] = useState(false);
  const [vatNoticeChecked, setVatNoticeChecked] = useState(false);
  const [vatNoticeSaving, setVatNoticeSaving] = useState(false);
  const extraNoteKey = `julyupay:extraNote:${sessionKey}`;

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(extraNoteKey);
      if (saved != null) setExtraNote(saved);
    } catch { /* Keep typing available when browser storage is restricted. */ }
    finally { setExtraNoteLoaded(true); }
  }, [extraNoteKey]);

  useEffect(() => {
    if (!extraNoteLoaded) return;
    try { window.localStorage.setItem(extraNoteKey, extraNote); }
    catch { /* Existing transfer storage behavior is unchanged. */ }
  }, [extraNote, extraNoteKey, extraNoteLoaded]);

  const nextPath = `/contracts/new/transfer?category=${service}&session_key=${encodeURIComponent(sessionKey)}${entryQuery}`;
  const vatCategory = vatNoticeCategoryKey(service);
  const hasVatNoticeAgreement = (category: string | ContractType = service) => {
    const keyCategory = vatNoticeCategoryKey(category);
    if (!VAT_NOTICE_TARGETS.has(keyCategory)) return true;
    try { return window.sessionStorage.getItem(vatNoticeStorageKey(sessionKey, keyCategory)) === "1"; }
    catch { return false; }
  };
  const requireVatNoticeAgreement = () => {
    if (hasVatNoticeAgreement()) return true;
    setVatNoticeChecked(false);
    setVatNoticeOpen(true);
    return false;
  };
  useEffect(() => {
    setVatNoticeChecked(false);
    setVatNoticeOpen(!hasVatNoticeAgreement(vatCategory));
  }, [sessionKey, vatCategory]);

  const agreeVatNotice = async () => {
    if (!vatNoticeChecked || vatNoticeSaving) return;
    setVatNoticeSaving(true);
    try {
      const resp = await authFetch("/api/agreements/vat-double-refund", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ category: vatCategory, session_key: sessionKey, notice_version: VAT_NOTICE_VERSION, agreed: true }),
      });
      const json = await resp.json().catch(() => null);
      if (!resp.ok || !json?.ok) throw new Error(json?.message || "동의 로그 저장에 실패했습니다.");
      try { window.sessionStorage.setItem(vatNoticeStorageKey(sessionKey, vatCategory), "1"); }
      catch { /* The next action rechecks whether the agreement is stored. */ }
      setVatNoticeOpen(false);
    } catch (error: unknown) {
      alert(error instanceof Error ? error.message : "동의 처리 중 오류가 발생했습니다.");
    } finally { setVatNoticeSaving(false); }
  };

  return (
    <div className="jp-payment-page">
      <PaymentStepFrame
        step={2}
        steps={["송금 금액", "정보·서류", "최종 확인"]}
        title="서류를 등록해 주세요"
        description={entryAmount !== null ? `송금액 ${entryAmount.toLocaleString("ko-KR")}원` : undefined}
        onBack={() => nav(entryBackPath, { replace: true })}
      >
        <div className="jp-flow-document-body">
          <Requirements type={service} className="jp-payment-requirements mt-0" sessionKey={sessionKey} onSatisfiedChange={setDocsOk} />
          <div className="mt-6">
            <label htmlFor="jp-request-note" className="jp-transfer-label">요청사항 <span className="font-normal text-slate-500">(선택)</span></label>
            <textarea id="jp-request-note" rows={3}
              className="min-h-[100px] w-full rounded-[14px] border border-slate-200 bg-white px-4 py-3 text-sm leading-6 text-slate-900 outline-none focus:border-[#f97316] focus:ring-4 focus:ring-[#f97316]/10"
              aria-label="거래 요청사항 (선택)" placeholder="추가로 전달할 내용이 있으면 적어 주세요."
              maxLength={500} value={extraNote} onChange={event => setExtraNote(event.target.value)} />
            <div className="jp-flow-note-count"><span>{extraNote.length}/500</span></div>
          </div>
          <div className="jp-flow-actions">
            <button type="button" className="jp-flow-primary" disabled={!docsOk || !isRequestVerificationComplete(user)}
              onClick={() => {
                if (!docsOk || !isRequestVerificationComplete(user) || !requireVatNoticeAgreement()) return;
                nav(nextPath, { state: { julyupayTransferFrom: location.pathname + location.search + location.hash } });
              }}>받는 분 정보 입력하기</button>
          </div>
        </div>
      </PaymentStepFrame>
      {vatNoticeOpen ? (
        <div className="jp-payment-dialog fixed inset-0 z-[220] flex items-center justify-center bg-slate-950/45 px-4">
          <div role="dialog" aria-modal="true" aria-labelledby="jp-vat-notice-title" className="w-full max-w-xl rounded-[22px] bg-white p-5 shadow-[0_24px_70px_rgba(15,23,42,0.24)] sm:p-6">
            <h2 id="jp-vat-notice-title" className="text-lg font-extrabold tracking-tight text-slate-950 sm:text-xl">
              {VAT_NOTICE_TITLE}
            </h2>
            <div className="mt-5 space-y-3 text-sm leading-6 text-slate-700 sm:text-base">
              <p>{VAT_NOTICE_TEXT[0]}</p>
              <p>{VAT_NOTICE_TEXT[1]}</p>
              <p className="font-extrabold text-rose-600">{VAT_NOTICE_TEXT[2]}</p>
            </div>

            <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-[16px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700">
              <input
                type="checkbox"
                checked={vatNoticeChecked}
                onChange={(e) => setVatNoticeChecked(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#f97316] focus:ring-[#f97316]"
              />
              <span>위 내용을 확인했으며, 부가세 이중 환급 금지 안내에 동의합니다.</span>
            </label>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => nav(entryBackPath, { replace: true })}
                className="rounded-[14px] border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
              >
                닫기
              </button>
              <button
                type="button"
                onClick={agreeVatNotice}
                disabled={!vatNoticeChecked || vatNoticeSaving}
                className="rounded-[14px] bg-[#f97316] px-4 py-2 text-sm font-bold text-white hover:bg-[#ea580c] disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {vatNoticeSaving ? "저장 중..." : "동의하고 진행"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
